import React from "react";
import { useNavigate } from "react-router-dom";
import { hoverCardHandlers } from "../lib/hoverCard";
import { BadgeCheck } from "lucide-react";
import { fileUrl } from "../lib/api";
import { rankStyle } from "../lib/ranks";
import { presenceMeta } from "../lib/presence";
import { useInvisible } from "../context/SocketContext";
import { frameClass } from "../lib/cosmetics";
import { onAvatarError } from "../lib/imgFallback";

const SIZES = { xs: "w-8 h-8 text-xs", sm: "w-10 h-10 text-sm", md: "w-12 h-12 text-base", lg: "w-20 h-20 text-2xl", xl: "w-28 h-28 text-4xl" };
// Item 29 — request each avatar at a size-appropriate width (~2x for retina) so member
// lists / Pack CRM / chat rosters fetch tiny thumbnails instead of one 200px image everywhere.
const AVATAR_W = { xs: 72, sm: 96, md: 112, lg: 192, xl: 256 };
const DOT_SIZES = { xs: "w-2.5 h-2.5", sm: "w-3 h-3", md: "w-3.5 h-3.5", lg: "w-5 h-5", xl: "w-6 h-6" };
const CHECK_SIZES = { xs: "w-3.5 h-3.5", sm: "w-4 h-4", md: "w-4.5 h-4.5", lg: "w-6 h-6", xl: "w-7 h-7" };

export const profilePath = (u) => (u?.is_mystic || u?.username === "mystic" ? "/mystic" : u?.username ? `/w/${u.username}` : null);

export function useProfileNav(user, enabled = true) {
  const navigate = useNavigate();
  const to = enabled ? profilePath(user) : null;
  if (!to) return {};
  const go = (e) => { e.stopPropagation(); e.preventDefault(); navigate(to); };
  return {
    role: "link", tabIndex: 0, "aria-label": `View ${user.name || user.username}'s profile`,
    onClick: go, onKeyDown: (e) => { if (e.key === "Enter") go(e); },
    ...hoverCardHandlers(user),
  };
}

export function UserLink({ user, children, className = "", ...props }) {
  const nav = useProfileNav(user);
  return <span {...nav} className={`${nav.onClick ? "cursor-pointer hover:underline underline-offset-2" : ""} ${className}`} data-testid="user-link" {...props}>{children ?? user?.name}</span>;
}

export function Avatar({ user, size = "md", ring = true, presence, showPresence = false, showVerified = false, link = true, className = "", onClick, eager = false, ...props }) {
  const nav = useProfileNav(user, link && !onClick);
  const inv = useInvisible();
  const ref = React.useRef(null);
  // An avatar inside a button/link is decorative there: drop its own link role so controls aren't nested.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.getAttribute("role") === "link" && el.parentElement?.closest("button,a[href],[role=button],[role=link]")) {
      el.removeAttribute("role"); el.removeAttribute("tabindex"); el.removeAttribute("aria-label");
    }
  });
  if (!user) return null;
  const style = rankStyle(user.rank);
  const initials = (user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const src = fileUrl(user.avatar_url, AVATAR_W[size] || 128);
  const p = inv.invisible && user.id === inv.meId ? "offline" : (presence || user.presence);
  const pm = presenceMeta(p);
  const frame = frameClass(user.equipped_frame);
  return (
    <div ref={ref} className={`relative shrink-0 rounded-full ${nav.onClick ? "cursor-pointer hover:brightness-110 transition-[filter]" : ""} ${className}`} data-avatar-link={nav.onClick ? "true" : undefined} {...nav} onClick={onClick || nav.onClick} {...props}>
      <div
        className={`relative rounded-full overflow-hidden flex items-center justify-center font-display font-bold ${SIZES[size]} ${frame ? frame : (ring ? `ring-2 ${style.ring} ${style.glow}` : "")}`}
      >
        {src ? (
          <img onError={onAvatarError} src={src} alt={user.name} loading={eager ? "eager" : "lazy"} decoding={eager ? "sync" : "async"} className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${style.bg} ${style.text}`}>{initials}</div>
        )}
      </div>
      {showVerified && user.verified && (
        <span
          className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 rounded-full bg-[#131B26] flex items-center justify-center z-10"
          title="Verified Wolf"
          data-testid="verified-check"
        >
          <BadgeCheck className={`${CHECK_SIZES[size]} text-sky-400 fill-[#131B26]`} strokeWidth={2.5} />
        </span>
      )}
      {showPresence && (
        <span
          className={`absolute bottom-0 right-0 rounded-full ring-2 ring-[#131B26] status-dot ${DOT_SIZES[size]} ${pm.color}`}
          title={pm.label}
          data-testid="presence-dot"
        />
      )}
    </div>
  );
}
